import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useNews } from "@/hooks/content";
import { useTranslatedList } from "@/hooks/useTranslatedList";
import Reveal from "@/components/base/Reveal";

export default function NewsPage() {
  const { t, i18n } = useTranslation();
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const locale = i18n.language === "fr" ? "fr-FR" : "en-US";

  const categories = [
    { value: "All", label: t("news.cat.all") },
    { value: "Breaking News", label: t("news.cat.breaking") },
    { value: "Local News", label: t("news.cat.local") },
    { value: "National News", label: t("news.cat.national") },
    { value: "International News", label: t("news.cat.international") },
    { value: "Politics", label: t("news.cat.politics") },
    { value: "Business", label: t("news.cat.business") },
    { value: "Technology", label: t("news.cat.technology") },
    { value: "Society", label: t("news.cat.society") },
    { value: "Religion", label: t("news.cat.religion") },
    { value: "Culture", label: t("news.cat.culture") },
    { value: "Sports", label: t("news.cat.sports") },
    { value: "Entertainment", label: t("news.cat.entertainment") },
    { value: "Lifestyle", label: t("news.cat.lifestyle") },
  ];

  const { items: articles } = useNews();
  const published = articles
    .filter((a) => a.status === "published")
    .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());

  useEffect(() => {
    document.title = "News | L+TV TÉLÉVISION";
  }, []);

  const filtered = published.filter((article) => {
    const matchesCategory = activeCategory === "All" || article.category === activeCategory;
    const matchesSearch =
      searchQuery === "" ||
      article.headline.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredRaw = filtered.find((a) => a.featured) || filtered[0];
  const others = filtered.filter((a) => a.id !== featuredRaw?.id);

  const itemsPerPage = 6;
  const totalPages = Math.ceil(others.length / itemsPerPage) || 1;
  const paginatedRaw = others.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const featured = useTranslatedList(featuredRaw ? [featuredRaw] : [], [
    "headline",
    "excerpt",
    "category",
  ])[0];
  const paginated = useTranslatedList(paginatedRaw, ["headline", "excerpt", "category"]);

  return (
    <div className="pt-20">
      {/* Header */}
      <div className="bg-primary-900 py-8">
        <div className="ltv-container">
          <Reveal direction="up">
            <h1 className="text-2xl md:text-3xl font-bold text-white">{t("news.title")}</h1>
            <p className="text-white/60 text-sm mt-1">{t("news.subtitle")}</p>
          </Reveal>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-white border-b border-background-200">
        <div className="ltv-container py-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="text"
                placeholder={t("news.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                className="ltv-input pl-10"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => { setActiveCategory(cat.value); setCurrentPage(1); }}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                  activeCategory === cat.value
                    ? "bg-primary-500 text-white"
                    : "bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="ltv-container py-8 md:py-12">
        {/* Featured Story */}
        {featured && (
          <Reveal direction="up">
            <div className="mb-10">
              <Link to={`/news/${featured.slug}`} className="ltv-card group block">
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="relative aspect-[16/10] md:aspect-auto overflow-hidden">
                    <img
                      src={featured.image}
                      alt={featured.headline}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-accent-500 text-white text-xs font-bold px-3 py-1 rounded">
                        {featured.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6 md:p-8 flex flex-col justify-center">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs text-foreground-500">
                        {new Date(featured.publishedAt).toLocaleDateString(locale, {
                          month: "long",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </span>
                      <span className="text-foreground-300">·</span>
                      <span className="text-xs text-foreground-500">{featured.readTime}</span>
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold text-foreground-900 leading-snug mb-3 group-hover:text-accent-600 transition-colors">
                      {featured.headline}
                    </h2>
                    <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                      {featured.excerpt}
                    </p>
                    <span className="text-sm font-semibold text-accent-500 inline-flex items-center gap-1">
                      {t("news.readFullStory")}
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              </Link>
            </div>
          </Reveal>
        )}

        {/* Latest Stories */}
        <h2 className="text-xl font-bold text-foreground-900 mb-5">{t("news.latestStories")}</h2>
        {paginated.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
            {paginated.map((article, index) => (
              <Reveal key={article.id} direction="up" delay={(index % 3) * 80}>
                <Link to={`/news/${article.slug}`} className="ltv-card group h-full">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.headline}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="bg-primary-500 text-white text-xs font-semibold px-2 py-1 rounded">
                        {article.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-foreground-900 leading-snug mb-2 line-clamp-2 group-hover:text-accent-600 transition-colors">
                      {article.headline}
                    </h3>
                    <div className="flex items-center gap-2 text-xs text-foreground-500">
                      <span>{article.author}</span>
                      <span>·</span>
                      <span>
                        {new Date(article.publishedAt).toLocaleDateString(locale, {
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-foreground-500">
            <i className="ri-newspaper-line text-4xl mb-3"></i>
            <p>{t("news.noArticles")}</p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 rounded-md text-sm font-medium bg-background-100 text-foreground-700 hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-9 h-9 rounded-md text-sm font-medium transition-colors ${
                  currentPage === page
                    ? "bg-primary-500 text-white"
                    : "bg-background-100 text-foreground-700 hover:bg-primary-50"
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 rounded-md text-sm font-medium bg-background-100 text-foreground-700 hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}